import * as THREE from 'three/webgpu';
import {
  Fn, vec2, vec3, vec4, float, abs, max, round, cos, sin,
  normalize, cross, texture, positionGeometry, uv, select, floor, mix,
  instancedBufferAttribute, varying, length,
  instancedDynamicBufferAttribute, uniform as tslUniform,
} from 'three/tsl';
import { QualityMode } from '@/utils/quality';

export type ImpostorMeta = {
  grid: number;
  hemi: boolean;
  radius: number;
  center: [number, number, number];
};

export type ImpostorMaterialOptions = {
  qualityLevel?: QualityMode;
  bendNormals?: boolean;
  /** See TreePlanterOptions.debugImpostors — flat magenta, no cutout. */
  debug?: boolean;
  /**
   * Blend the three baked views around the current direction rather than
   * snapping to the nearest. Two extra texture taps; the knob exists so the
   * cheap path is one flag away if it ever measures badly on a headset.
   */
  blendFrames?: boolean;
  /** Baked course lightmap, so trees sit in the same light as the ground. */
  lightmap?: THREE.Texture;
  /** Course extent the lightmap spans, in metres. */
  lightmapWorldSize?: number;
  /** 1 = full baked darkness. */
  lightmapStrength?: number;
};

export function createImpostorMaterial(
  map: THREE.Texture,
  meta: ImpostorMeta,
  posScaleAttr: THREE.InstancedBufferAttribute, // xyz = quad center (world), w = scale (0 = hidden)
  yawAttr: THREE.InstancedBufferAttribute,      // baked per-tree Y rotation
  options: ImpostorMaterialOptions = {},
) {
  const {
    qualityLevel,
    bendNormals = true,
    debug = false,
    blendFrames = true,
    lightmap,
    lightmapWorldSize = 1000,
    lightmapStrength = 0.85,
  } = options;
  const N = meta.grid;
  const mat = new THREE.MeshStandardNodeMaterial({
    roughness: 1,
    metalness: 0,
    side: THREE.DoubleSide,
  });

  // Match the batch cutout logic: no MSAA on Low → A2C unavailable
  // mat.alphaTest = 0.4;
  // mat.alphaToCoverage = true;
  if (debug) {
    // No cutout at all: if the quads are being drawn, they cannot be hidden by
    // an atlas sample that came back transparent.
    mat.alphaTest = 0;
    mat.alphaToCoverage = false;
  } else if (qualityLevel === QualityMode.Low) {
    mat.alphaTest = 0.6;
    mat.alphaToCoverage = false;
  } else {
    mat.alphaTest = 0.4;
    mat.alphaToCoverage = true;
  }
  mat.transparent = false;
  mat.depthWrite = true;

  // const inst = instancedBufferAttribute(posScaleAttr);
  // const yaw = instancedBufferAttribute(yawAttr);
  const inst = instancedDynamicBufferAttribute<'vec4'>(posScaleAttr, 'vec4');
  const yaw = instancedBufferAttribute<'float'>(yawAttr, 'float');

  // The viewer, already in this mesh's own space, handed over from JS.
  //
  // `inst.xyz` is planted in treeGroup-local coordinates, so the facing maths
  // needs the viewer in that same frame — and course-mr re-poses treeGroup's
  // parent onto the player's mat every shot, so world and local coincide only
  // by accident. Every in-shader route to that number has now been tried and
  // failed on a headset: the `cameraPosition` built-in throws under XR's
  // ArrayCamera (see shaders/viewer.ts), and deriving it with
  // `modelWorldMatrixInverse` depends on a shared module-level uniform driven
  // by an onObjectUpdate callback, which is exactly the sort of per-object
  // bookkeeping that differs between the WebGPU and WebGL backends — and the
  // headset is forced onto WebGL (FuseRenderer, options.xr).
  //
  // So it is a plain uniform now, with no builtin anywhere in the path.
  // TreePlanter already receives the head converted into course space every
  // frame for LOD, which is precisely this vector, so this costs one Vector3
  // copy per frame and removes the whole class of failure.
  const camLocal = tslUniform(new THREE.Vector3());
  const toCam = camLocal.sub(inst.xyz);

  // `cross(UP, fwd)` collapses to a zero vector when the view is straight down
  // the Y axis, and normalising that yields NaN — which takes the whole quad
  // with it, not just its orientation. Trees are Y-billboards in practice, so
  // build the basis from the HORIZONTAL view direction and there is only one
  // degenerate case left: a viewer exactly on the trunk's axis.
  const flat = vec3(toCam.x, 0, toCam.z);
  const flatLen = length(flat);
  const safeFlat = select(flatLen.greaterThan(1e-4), flat, vec3(0, 0, 1));

  const fwd = varying(normalize(select(length(toCam).greaterThan(1e-4), toCam, vec3(0, 0, 1))));
  // cross((0,1,0), f) == (f.z, 0, -f.x); taken from the flattened direction so
  // it stays unit and horizontal however steeply the player looks down.
  const right = varying(normalize(vec3(safeFlat.z, 0, safeFlat.x.negate())));
  const up = varying(normalize(cross(fwd, right)));

  // Spherical billboard, expanded in object space (modelViewMatrix carries it
  // to world/view as usual).
  mat.positionNode = Fn(() => {
    const half = float(meta.radius).mul(inst.w);
    return inst.xyz
      .add(right.mul(positionGeometry.x.mul(half)))
      .add(up.mul(positionGeometry.y.mul(half)));
  })();

  // Frame selection: view dir in tree-local space (undo baked yaw) →
  // hemi-octahedral encode (exact inverse of the bake's hemiOctaDir) → sample
  // the baked view(s) for that direction.
  /**
   * The baked course shadow at this tree's foot.
   *
   * Course surfaces already multiply this in (`applyLightmapShadow`), trees did
   * not — so a tree standing in the shade of the hill behind it was lit as
   * brightly as one in the open, which is a large part of why the billboards
   * read as pasted onto the scene rather than standing in it.
   *
   * Sampled at `inst.xz` — the quad's CENTRE, in treeGroup-local space — not at
   * `positionWorld` like the surfaces do. Two reasons. One value per tree is
   * what a billboard wants anyway: sampling per fragment would sweep the
   * lightmap across the quad's whole footprint and pull in whatever the bake
   * says about the ground several metres either side of the trunk. And local
   * space is the frame the lightmap was baked in, whereas `positionWorld` for
   * anything under courseRoot moves with the re-pose.
   */
  const lightmapFactor = lightmap
    ? mix(float(1), texture(lightmap, inst.xz.div(float(lightmapWorldSize))).r,
      float(lightmapStrength))
    : null;

  mat.colorNode = debug ? vec4(1, 0, 1, 1) : Fn(() => {
    const c = cos(yaw), s = sin(yaw);
    const lx = fwd.x.mul(c).sub(fwd.z.mul(s));
    const lz = fwd.x.mul(s).add(fwd.z.mul(c));
    const ly = max(fwd.y, float(0.02)); // clamp to hemisphere

    const sum = abs(lx).add(ly).add(abs(lz));
    const px = lx.div(sum), pz = lz.div(sum);
    const g = vec2(px.add(pz), px.sub(pz)).mul(0.5).add(0.5);

    // Continuous position in the atlas grid. The cell corners either side of
    // it are the baked views nearest this direction.
    // If trees render upside-down, replace uv() with vec2(uv().x, uv().y.oneMinus())
    const gf = g.mul(N - 1);
    const frame = (cell: any) =>
      texture(map, cell.clamp(0, N - 1).add(uv()).div(N));
    // Baked shadow on the rgb only — alpha is the cutout and must not be dimmed.
    const shade = (c: any) => (lightmapFactor ? vec4(c.rgb.mul(lightmapFactor), c.a) : c);

    if (!blendFrames) return shade(frame(round(gf)));

    /**
     * Blend the three baked views surrounding this direction instead of
     * snapping to the nearest one.
     *
     * A 4x4 hemi-octahedral atlas is only 16 views over the whole upper
     * hemisphere, so a single snapped tap changes frame every ~22° of yaw and
     * the whole canopy visibly jumps to a different photo as you turn your
     * head. In a headset that reads as the tree twitching, which is far more
     * distracting than it sounds on a flat screen.
     *
     * The grid square splits along its anti-diagonal into two triangles; the
     * three corners of whichever triangle contains this point are the natural
     * interpolants, weighted barycentrically. Two extra texture taps, and the
     * weights sum to exactly 1 on both sides of the diagonal so there is no
     * seam where the triangle selection flips.
     */
    const base = floor(gf);
    const f = gf.sub(base);
    const upper = f.x.add(f.y).greaterThan(1);

    const w0 = select(upper, f.x.add(f.y).sub(1), float(1).sub(f.x).sub(f.y));
    const w1 = select(upper, float(1).sub(f.y), f.x);
    const w2 = select(upper, float(1).sub(f.x), f.y);
    const c0 = select(upper, vec2(1, 1), vec2(0, 0));

    const t0 = frame(base.add(c0));
    const t1 = frame(base.add(vec2(1, 0)));
    const t2 = frame(base.add(vec2(0, 1)));

    // Weight PREMULTIPLIED colour, then undo it. Blending straight alpha drags
    // the black RGB of the atlas's transparent texels into every silhouette
    // edge, ringing the whole canopy in dark fringes — and on foliage, which is
    // nearly all silhouette, that is the entire tree.
    const a0 = t0.a.mul(w0), a1 = t1.a.mul(w1), a2 = t2.a.mul(w2);
    const a = a0.add(a1).add(a2);
    const rgb = t0.rgb.mul(a0).add(t1.rgb.mul(a1)).add(t2.rgb.mul(a2));
    return shade(vec4(rgb.div(a.max(1e-4)), a));
  })();

  if (bendNormals) {
    // Fake spherical canopy normal: bend outward from quad center so the
    // sun side lights up and the far side falls into shade.
    mat.normalNode = normalize(
      right.mul(positionGeometry.x.mul(0.7))
        .add(up.mul(positionGeometry.y.mul(0.7)))
        .add(fwd),
    );
  }

  // The caller must feed this every frame, in treeGroup-local space, or every
  // billboard faces wherever the viewer was when the material was built.
  return { material: mat, camLocal };
}